import { useCallback, useEffect, useRef, useState } from "react";

const DEFAULT_OPTIONS = {
  widthRatio: 0.8,
  heightRatio: 0.75,
  minWidth: 400,
  minHeight: 280,
  margin: 16,
  /** localStorage key; when set, the panel reopens at its last size/position. */
  storageKey: null,
};

function readStoredBounds(config) {
  if (!config.storageKey) return null;
  try {
    const raw = localStorage.getItem(config.storageKey);
    if (!raw) return null;
    const b = JSON.parse(raw);
    if (![b.x, b.y, b.width, b.height].every(Number.isFinite)) return null;
    // Re-clamp: the window may be smaller than when the bounds were saved.
    const size = clampSize(
      b.width,
      b.height,
      config.margin,
      config.margin,
      config.minWidth,
      config.minHeight,
      config.margin
    );
    const pos = clampPosition(b.x, b.y, size.width, size.height, config.margin);
    return { ...size, ...pos };
  } catch {
    return null;
  }
}

function getInitialBounds(options = {}) {
  const config = { ...DEFAULT_OPTIONS, ...options };
  const stored = readStoredBounds(config);
  if (stored) return stored;
  const { widthRatio, heightRatio, minWidth, minHeight, margin } = config;
  const width = Math.max(minWidth, Math.round(window.innerWidth * widthRatio));
  const height = Math.max(
    minHeight,
    Math.round(window.innerHeight * heightRatio)
  );
  return {
    x: Math.max(margin, Math.round((window.innerWidth - width) / 2)),
    y: Math.max(margin, Math.round((window.innerHeight - height) / 2)),
    width,
    height,
  };
}

function clampPosition(x, y, width, height, margin) {
  const maxX = Math.max(margin, window.innerWidth - width - margin);
  const maxY = Math.max(margin, window.innerHeight - height - margin);
  return {
    x: Math.min(Math.max(margin, x), maxX),
    y: Math.min(Math.max(margin, y), maxY),
  };
}

function clampSize(width, height, x, y, minWidth, minHeight, margin) {
  const maxWidth = Math.max(minWidth, window.innerWidth - x - margin);
  const maxHeight = Math.max(minHeight, window.innerHeight - y - margin);
  return {
    width: Math.min(Math.max(minWidth, width), maxWidth),
    height: Math.min(Math.max(minHeight, height), maxHeight),
  };
}

/** Adjust x/y when resizing from top or left edges (host is positioned separately). */
function applyResizeFromDirection(start, direction, delta) {
  let { x, y, width, height } = start;
  width = start.width + delta.width;
  height = start.height + delta.height;

  if (direction === "left" || direction === "topLeft" || direction === "bottomLeft") {
    x = start.x - delta.width;
  }
  if (direction === "top" || direction === "topLeft" || direction === "topRight") {
    y = start.y - delta.height;
  }

  return { x, y, width, height };
}

/**
 * Drag + resize state for a fixed-position floating panel (no modal mask).
 * Resets to centered default bounds whenever `open` becomes true.
 */
export default function useFloatingPanel(open, options = {}) {
  const config = { ...DEFAULT_OPTIONS, ...options };
  const [bounds, setBounds] = useState(() => getInitialBounds(config));
  const boundsRef = useRef(bounds);
  boundsRef.current = bounds;
  const dragRef = useRef(null);
  const resizeStartRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (open) setBounds(getInitialBounds(config));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Persist bounds (debounced — drag/resize update on every mousemove).
  const persistTimerRef = useRef(0);
  useEffect(() => {
    if (!open || !config.storageKey) return undefined;
    clearTimeout(persistTimerRef.current);
    persistTimerRef.current = setTimeout(() => {
      try {
        localStorage.setItem(config.storageKey, JSON.stringify(bounds));
      } catch {
        // Storage full/unavailable — panel still works, just won't remember.
      }
    }, 250);
    return () => clearTimeout(persistTimerRef.current);
  }, [bounds, open, config.storageKey]);

  const onMouseMove = useCallback(
    (e) => {
      const drag = dragRef.current;
      if (!drag) return;

      const next = clampPosition(
        drag.originX + (e.clientX - drag.startX),
        drag.originY + (e.clientY - drag.startY),
        boundsRef.current.width,
        boundsRef.current.height,
        config.margin
      );
      setBounds((prev) => ({ ...prev, ...next }));
    },
    [config.margin]
  );

  const onMouseUp = useCallback(() => {
    dragRef.current = null;
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);
  }, [onMouseMove]);

  const onHeaderMouseDown = useCallback(
    (e) => {
      if (e.target.closest(".floating-panel__close")) return;
      e.preventDefault();
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        originX: boundsRef.current.x,
        originY: boundsRef.current.y,
      };
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    },
    [onMouseMove, onMouseUp]
  );

  const onResizeStart = useCallback(() => {
    resizeStartRef.current = { ...boundsRef.current };
  }, []);

  const onResize = useCallback(
    (_event, direction, _ref, delta) => {
      const start = resizeStartRef.current;
      if (!start) return;

      const resized = applyResizeFromDirection(start, direction, delta);
      const nextSize = clampSize(
        resized.width,
        resized.height,
        resized.x,
        resized.y,
        config.minWidth,
        config.minHeight,
        config.margin
      );
      const nextPos = clampPosition(
        resized.x,
        resized.y,
        nextSize.width,
        nextSize.height,
        config.margin
      );
      setBounds({ ...nextSize, ...nextPos });
    },
    [config.margin, config.minHeight, config.minWidth]
  );

  useEffect(
    () => () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    },
    [onMouseMove, onMouseUp]
  );

  return {
    bounds,
    panelRef,
    minWidth: config.minWidth,
    minHeight: config.minHeight,
    onHeaderMouseDown,
    onResizeStart,
    onResize,
  };
}
